import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { Card } from '../components/ui-kit';
import {
  getNotificationTemplates,
  getNotificationTemplateCategories,
  updateNotificationTemplate,
} from '../api/notificationTemplates';

/**
 * Человекочитаемые названия категорий. Это только оформление — сам список
 * категорий приходит с сервера, из тех, что реально есть в шаблонах.
 * Для незнакомой категории название собирается из её кода, поэтому новый
 * сервис появится в фильтре сам, без правки этого файла.
 */
const CATEGORY_LABELS = {
  'talent-service': 'Talent',
  'jobs-service': 'Jobs',
  'account-service': 'Account',
  'attendance-service': 'Attendance',
  'task-manager-service': 'Tasks',
  'libraries-service': 'Libraries',
  'support-service': 'Support',
  'auth-service': 'Auth',
};

const categoryLabel = (value) =>
  CATEGORY_LABELS[value]
  || String(value || '')
    .replace(/-service$/, '')
    .replace(/[-_]/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const ROUTING_LABELS = {
  user: 'User',
  role: 'Role',
  'user+role': 'User + Role',
};

const ROUTING_COLORS = {
  user: 'primary',
  role: 'warning',
  'user+role': 'secondary',
};

const TYPE_COLORS = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

export default function NotificationTemplatesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [routingFilter, setRoutingFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  // Категории тянем отдельно и без фильтров: если брать их из текущей выборки,
  // список схлопнется до одной сразу после выбора категории
  const { data: categoriesData } = useQuery({
    queryKey: ['notification-template-categories'],
    queryFn: getNotificationTemplateCategories,
    staleTime: 5 * 60 * 1000,
  });
  const categories = categoriesData?.categories || [];

  const { data, isLoading } = useQuery({
    queryKey: ['notification-templates', search, categoryFilter, routingFilter, activeFilter],
    queryFn: () =>
      getNotificationTemplates({
        search: search || undefined,
        category: categoryFilter || undefined,
        routing: routingFilter || undefined,
        isActive: activeFilter !== '' ? activeFilter : undefined,
        limit: 100,
      }),
    keepPreviousData: true,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => updateNotificationTemplate(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['notification-templates']);
      toast.success('Template updated');
    },
    onError: () => toast.error('Failed to update template'),
  });

  const handleToggleActive = useCallback(
    (id, currentlyActive) => {
      toggleMutation.mutate({ id, isActive: !currentlyActive });
    },
    [toggleMutation]
  );

  const templates = data?.templates || [];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Notification Templates
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by event type or title..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((value) => (
                <MenuItem key={value} value={value}>
                  {categoryLabel(value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Routing</InputLabel>
            <Select
              value={routingFilter}
              label="Routing"
              onChange={(e) => setRoutingFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="role">Role</MenuItem>
              <MenuItem value="user+role">User + Role</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Table */}
      <Card>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">No templates found</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Event Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Routing</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Segments</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow
                    key={tpl._id}
                    hover
                    sx={{ opacity: tpl.isActive ? 1 : 0.5 }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {tpl.eventType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {tpl.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={categoryLabel(tpl.category)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ROUTING_LABELS[tpl.routing] || tpl.routing}
                        size="small"
                        color={ROUTING_COLORS[tpl.routing] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(tpl.segments || []).map((seg) => (
                          <Chip key={seg} label={seg} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tpl.type}
                        size="small"
                        color={TYPE_COLORS[tpl.type] || 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={tpl.isActive ? 'Click to deactivate' : 'Click to activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(tpl._id, tpl.isActive)}
                          color={tpl.isActive ? 'success' : 'default'}
                        >
                          {tpl.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit template">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/notification-templates/${tpl._id}`)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
    </Box>
  );
}
